import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categoryShowcaseService } from '../services/categoryShowcaseService';
import '../styles/categoryShowcase.css';
import '../styles/categoryDetail.css';

interface SubcategoryItem {
    _id: string;
    name: string;
    slug: string;
}

const GRADIENT_COUNT = 6;

const ChevronRight: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18l6-6-6-6" />
    </svg>
);

const SubcategoryCard: React.FC<{ sub: SubcategoryItem; categorySlug: string; index: number }> = ({ sub, categorySlug, index }) => {
    const gradientClass = `cs-card-img-wrap--g${index % GRADIENT_COUNT}`;
    const href = `/products?category=${encodeURIComponent(categorySlug)}&subcategory=${encodeURIComponent(sub.slug)}`;

    return (
        <Link to={href} className="cs-card">
            <div className={`cs-card-img-wrap ${gradientClass}`}>
                <span className="cs-card-icon" role="img" aria-label={sub.name}>🎨</span>
            </div>
            <div className="cs-card-body">
                <span className="cs-card-name">{sub.name}</span>
                <span className="cs-card-arrow" aria-hidden="true">
                    <ChevronRight />
                </span>
            </div>
        </Link>
    );
};

const SkeletonCard: React.FC = () => (
    <div className="cs-skeleton-card">
        <div className="cs-skeleton-img" />
        <div className="cs-skeleton-body">
            <div className="cs-skeleton-text" />
            <div className="cs-skeleton-circle" />
        </div>
    </div>
);

const CategoryDetailPage: React.FC = () => {
    const { categorySlug = '' } = useParams<{ categorySlug: string }>();

    const [categoryName, setCategoryName] = useState('');
    const [subcategories, setSubcategories] = useState<SubcategoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!categorySlug) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await categoryShowcaseService.fetchSubcategories(categorySlug);
                const res = data as { categoryName: string; subcategories: SubcategoryItem[] };
                if (!cancelled) {
                    setCategoryName(res.categoryName || categorySlug);
                    setSubcategories(res.subcategories);
                }
            } catch {
                if (!cancelled) setError('Failed to load subcategories');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [categorySlug]);

    return (
        <>
            {/* Hero */}
            <div className="cs-page-hero">
                <div className="container">
                    <h1 className="cs-page-hero-title">{categoryName || categorySlug}</h1>
                    <p className="cs-page-hero-sub">
                        Browse subcategories and discover handcrafted products
                    </p>
                </div>
            </div>

            {/* Breadcrumb */}
            <div style={{ padding: '32px 0 0' }}>
                <div className="container">
                    <nav className="cs-breadcrumb" aria-label="Breadcrumb">
                        <Link to="/">Home</Link>
                        <span className="cs-breadcrumb-sep" aria-hidden="true">›</span>
                        <Link to="/categories">Categories</Link>
                        <span className="cs-breadcrumb-sep" aria-hidden="true">›</span>
                        <span aria-current="page">{categoryName || categorySlug}</span>
                    </nav>
                </div>
            </div>

            {/* Subcategory grid */}
            <section className="cs-section">
                <div className="container">
                    <div className="cs-header">
                        <span className="cs-eyebrow">SUBCATEGORIES</span>
                        <h2 className="cs-title">{categoryName}</h2>
                        <p className="cs-subtitle">
                            Select a subcategory to explore products
                        </p>
                    </div>

                    {loading && (
                        <div className="cs-grid">
                            {Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
                        </div>
                    )}

                    {!loading && error && (
                        <p className="cs-empty">{error}</p>
                    )}

                    {!loading && !error && subcategories.length === 0 && (
                        <div className="csd-empty">
                            <span className="csd-empty-icon">🛍️</span>
                            <p className="csd-empty-text">No subcategories yet for this category.</p>
                            <Link to="/categories" className="cs-page-cta-btn" style={{ marginTop: 16, display: 'inline-flex' }}>
                                Back to Categories
                            </Link>
                        </div>
                    )}

                    {!loading && !error && subcategories.length > 0 && (
                        <div className="cs-grid">
                            {subcategories.map((sub, i) => (
                                <SubcategoryCard
                                    key={sub._id}
                                    sub={sub}
                                    categorySlug={categorySlug}
                                    index={i}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <div className="container" style={{ paddingBottom: '56px' }}>
                <div className="cs-page-cta">
                    <p className="cs-page-cta-text">Can't find what you're looking for?</p>
                    <Link to="/request-a-quote" className="cs-page-cta-btn">
                        Request a Custom Quote
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </Link>
                </div>
            </div>
        </>
    );
};

export default CategoryDetailPage;
